import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { DataService } from "../services/supabase";
import { Transaction } from "../types";
import { deleteCardImages, getSignedImageUrls, uploadCardImage } from "../services/cardImageService";

type CardCatalogFieldKey =
  | "card_number"
  | "subject"
  | "league"
  | "insert_name"
  | "parallel"
  | "numbering";

const emptyCardDetailFields: Record<CardCatalogFieldKey, string> = {
  card_number: "",
  subject: "",
  league: "",
  insert_name: "",
  parallel: "",
  numbering: "",
};

const MAX_OTHER_IMAGES = 3;
const MAX_TOTAL_CARDS = 999;

function CardCatalogFields({
  idPrefix,
  values,
  onFieldChange,
}: {
  idPrefix: string;
  values: Record<CardCatalogFieldKey, string>;
  onFieldChange: (key: CardCatalogFieldKey, value: string) => void;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-card_number`}>Card #</Label>
        <Input
          id={`${idPrefix}-card_number`}
          value={values.card_number}
          onChange={(e) => onFieldChange("card_number", e.target.value)}
          placeholder="e.g., 23, RC-1"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-subject`}>Player / Character</Label>
        <Input
          id={`${idPrefix}-subject`}
          value={values.subject}
          onChange={(e) => onFieldChange("subject", e.target.value)}
          placeholder="e.g., Shohei Ohtani, Charizard"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-league`}>League</Label>
        <Input
          id={`${idPrefix}-league`}
          value={values.league}
          onChange={(e) => onFieldChange("league", e.target.value)}
          placeholder="e.g., NBA, NCAA, Premier League"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-insert_name`}>Insert / Subset</Label>
        <Input
          id={`${idPrefix}-insert_name`}
          value={values.insert_name}
          onChange={(e) => onFieldChange("insert_name", e.target.value)}
          placeholder="e.g., Image Variation, Silver Prizm"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-parallel`}>Parallel</Label>
        <Input
          id={`${idPrefix}-parallel`}
          value={values.parallel}
          onChange={(e) => onFieldChange("parallel", e.target.value)}
          placeholder="e.g., Gold /50, Blue Wave"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-numbering`}>Print run / Numbering</Label>
        <Input
          id={`${idPrefix}-numbering`}
          value={values.numbering}
          onChange={(e) => onFieldChange("numbering", e.target.value)}
          placeholder="e.g., 12/99, 1st Edition"
        />
      </div>
    </div>
  );
}

interface EditInventoryCardProps {
  dataService?: DataService | null;
  transactions?: Transaction[];
  onTransactionsUpdate?: (transactions: Transaction[]) => void;
}

const EditInventoryCard = ({ dataService, transactions = [], onTransactionsUpdate }: EditInventoryCardProps) => {
  const navigate = useNavigate();
  const { transactionId = "" } = useParams<{ transactionId: string }>();
  const isCreateMode = !transactionId;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [existingImageUrls, setExistingImageUrls] = useState<Record<string, string>>({});
  const [selectedImage, setSelectedImage] = useState<{ label: string; src: string } | null>(null);

  const editingTransaction = useMemo(
    () => transactions.find((transaction) => transaction.id === transactionId) || null,
    [transactions, transactionId]
  );

  const [editData, setEditData] = useState({
    card: "",
    sport: "",
    purchaseDate: "",
    purchasePlatform: "",
    purchasePrice: "",
    year: "",
    manufacturer: "",
    set: "",
    ...emptyCardDetailFields,
    other: "",
    grade: "",
    gradingOrderId: "",
    boughtFrom: "",
    notes: "",
    numberOfCards: 1,
    frontImagePath: null as string | null,
    backImagePath: null as string | null,
    otherImagePaths: [] as string[],
    frontImageFile: null as File | null,
    backImageFile: null as File | null,
    otherImageFiles: [] as File[],
  });

  useEffect(() => {
    if (!editingTransaction || isCreateMode) return;
    setEditData({
      card: editingTransaction.card || "",
      sport: editingTransaction.sport || "",
      purchaseDate: editingTransaction.purchase_date || "",
      purchasePlatform: editingTransaction.purchase_platform || "",
      purchasePrice: typeof editingTransaction.cost === "number" ? editingTransaction.cost.toString() : editingTransaction.cost || "0",
      year: editingTransaction.year?.toString() || "",
      manufacturer: editingTransaction.manufacturer || "",
      set: editingTransaction.set || "",
      card_number: editingTransaction.card_number || "",
      subject: editingTransaction.subject || "",
      league: editingTransaction.league || "",
      insert_name: editingTransaction.insert_name || "",
      parallel: editingTransaction.parallel || "",
      numbering: editingTransaction.numbering || "",
      other: editingTransaction.other || "",
      grade: editingTransaction.grade || "",
      gradingOrderId: editingTransaction.grading_order_id || "",
      boughtFrom: editingTransaction.bought_from || "",
      notes: editingTransaction.notes || "",
      numberOfCards: 1,
      frontImagePath: editingTransaction.front_image_path || null,
      backImagePath: editingTransaction.back_image_path || null,
      otherImagePaths: Array.isArray(editingTransaction.other_image_paths) ? editingTransaction.other_image_paths : [],
      frontImageFile: null,
      backImageFile: null,
      otherImageFiles: [],
    });
  }, [editingTransaction, isCreateMode]);

  useEffect(() => {
    const imagePaths = [
      editData.frontImagePath,
      editData.backImagePath,
      ...editData.otherImagePaths,
    ].filter(Boolean) as string[];

    if (imagePaths.length === 0) {
      setExistingImageUrls({});
      return;
    }

    let isMounted = true;
    getSignedImageUrls(imagePaths)
      .then((urls) => {
        if (!isMounted) return;
        const nextMap: Record<string, string> = {};
        imagePaths.forEach((path, index) => {
          nextMap[path] = urls[index];
        });
        setExistingImageUrls(nextMap);
      })
      .catch(() => {
        if (isMounted) {
          setExistingImageUrls({});
        }
      });

    return () => {
      isMounted = false;
    };
  }, [editData.frontImagePath, editData.backImagePath, editData.otherImagePaths]);

  const normalizeOtherImageFiles = (files: File[] = []) => files.filter(Boolean).slice(0, MAX_OTHER_IMAGES);

  const handleSave = async () => {
    if (!dataService) {
      setErrorMessage("Data service unavailable.");
      return;
    }
    if (!editData.card || !editData.purchaseDate || !editData.purchasePrice) {
      setErrorMessage("Please fill in all required fields (Card Name, Purchase Date, Purchase Price).");
      return;
    }

    const parsedPrice = parseFloat(editData.purchasePrice) || 0;
    const numberOfCards = Math.min(Math.max(1, editData.numberOfCards || 1), MAX_TOTAL_CARDS);
    setErrorMessage(null);
    setIsSaving(true);

    try {
      if (isCreateMode) {
        for (let i = 0; i < numberOfCards; i += 1) {
          const transactionData = {
            card: editData.card,
            sport: editData.sport || null,
            purchase_date: editData.purchaseDate,
            purchase_platform: editData.purchasePlatform || null,
            cost: parsedPrice,
            year: editData.year ? parseInt(editData.year, 10) : null,
            manufacturer: editData.manufacturer || null,
            set: editData.set || null,
            card_number: editData.card_number || null,
            subject: editData.subject || null,
            league: editData.league || null,
            insert_name: editData.insert_name || null,
            parallel: editData.parallel || null,
            numbering: editData.numbering || null,
            other: editData.other || null,
            grade: editData.grade || null,
            grading_order_id: editData.gradingOrderId || null,
            bought_from: editData.boughtFrom || null,
            notes: editData.notes || null,
          };

          const result = await dataService.addTransaction(transactionData);
          const createdId = (result as any)?.transaction_id || (result as any)?.data?.transaction_id;
          if (!createdId) continue;

          const imageUpdates: Record<string, any> = {};
          if (editData.frontImageFile) {
            imageUpdates.front_image_path = await uploadCardImage(editData.frontImageFile, createdId, "front");
          }
          if (editData.backImageFile) {
            imageUpdates.back_image_path = await uploadCardImage(editData.backImageFile, createdId, "back");
          }
          const otherFiles = normalizeOtherImageFiles(editData.otherImageFiles);
          if (otherFiles.length > 0) {
            imageUpdates.other_image_paths = await Promise.all(
              otherFiles.map((file) => uploadCardImage(file, createdId, "other"))
            );
          }
          if (Object.keys(imageUpdates).length > 0) {
            await dataService.updateTransaction(createdId, imageUpdates);
          }
        }

        if (onTransactionsUpdate) {
          const allTransactions = await dataService.getTransactions();
          onTransactionsUpdate(allTransactions);
        }
      } else {
        if (!editingTransaction?.id) {
          setErrorMessage("Card unavailable for editing.");
          return;
        }

        const transactionIdToSave = editingTransaction.id;
        const updates: Record<string, any> = {};
        if (editData.card !== editingTransaction.card) updates.card = editData.card;
        if (editData.sport !== editingTransaction.sport) updates.sport = editData.sport || null;
        if (editData.purchaseDate !== editingTransaction.purchase_date) updates.purchase_date = editData.purchaseDate;
        if (editData.purchasePlatform !== editingTransaction.purchase_platform) updates.purchase_platform = editData.purchasePlatform || null;
        if (parsedPrice !== (typeof editingTransaction.cost === "number" ? editingTransaction.cost : parseFloat(editingTransaction.cost || "0"))) {
          updates.cost = parsedPrice;
        }
        if (editData.year !== (editingTransaction.year?.toString() || "")) updates.year = editData.year ? parseInt(editData.year, 10) : null;
        if (editData.manufacturer !== (editingTransaction.manufacturer || "")) updates.manufacturer = editData.manufacturer || null;
        if (editData.set !== (editingTransaction.set || "")) updates.set = editData.set || null;
        if (editData.card_number !== (editingTransaction.card_number || "")) updates.card_number = editData.card_number || null;
        if (editData.subject !== (editingTransaction.subject || "")) updates.subject = editData.subject || null;
        if (editData.league !== (editingTransaction.league || "")) updates.league = editData.league || null;
        if (editData.insert_name !== (editingTransaction.insert_name || "")) updates.insert_name = editData.insert_name || null;
        if (editData.parallel !== (editingTransaction.parallel || "")) updates.parallel = editData.parallel || null;
        if (editData.numbering !== (editingTransaction.numbering || "")) updates.numbering = editData.numbering || null;
        if (editData.other !== (editingTransaction.other || "")) updates.other = editData.other || null;
        if (editData.grade !== (editingTransaction.grade || "")) updates.grade = editData.grade || null;
        if (editData.gradingOrderId !== (editingTransaction.grading_order_id || "")) updates.grading_order_id = editData.gradingOrderId || null;
        if (editData.boughtFrom !== (editingTransaction.bought_from || "")) updates.bought_from = editData.boughtFrom || null;
        if (editData.notes !== (editingTransaction.notes || "")) updates.notes = editData.notes || null;

        const originalOtherPaths = Array.isArray(editingTransaction.other_image_paths) ? editingTransaction.other_image_paths : [];
        const nextOtherPaths = editData.otherImagePaths.slice(0, MAX_OTHER_IMAGES);
        const removedOtherPaths = originalOtherPaths.filter((path) => !nextOtherPaths.includes(path));
        const removedFrontPath = editingTransaction.front_image_path && !editData.frontImagePath ? editingTransaction.front_image_path : null;
        const removedBackPath = editingTransaction.back_image_path && !editData.backImagePath ? editingTransaction.back_image_path : null;
        const imagesToRemove = [removedFrontPath, removedBackPath, ...removedOtherPaths];
        if (imagesToRemove.length > 0) {
          await deleteCardImages(imagesToRemove);
        }

        if (editData.frontImageFile) {
          if (editingTransaction.front_image_path) {
            await deleteCardImages([editingTransaction.front_image_path]);
          }
          updates.front_image_path = await uploadCardImage(editData.frontImageFile, transactionIdToSave, "front");
        } else if (editData.frontImagePath === null && editingTransaction.front_image_path) {
          updates.front_image_path = null;
        }

        if (editData.backImageFile) {
          if (editingTransaction.back_image_path) {
            await deleteCardImages([editingTransaction.back_image_path]);
          }
          updates.back_image_path = await uploadCardImage(editData.backImageFile, transactionIdToSave, "back");
        } else if (editData.backImagePath === null && editingTransaction.back_image_path) {
          updates.back_image_path = null;
        }

        const normalizedOtherFiles = normalizeOtherImageFiles(editData.otherImageFiles);
        if (normalizedOtherFiles.length > 0 || removedOtherPaths.length > 0) {
          const uploadedOtherPaths = normalizedOtherFiles.length > 0
            ? await Promise.all(normalizedOtherFiles.map((file) => uploadCardImage(file, transactionIdToSave, "other")))
            : [];
          updates.other_image_paths = [...nextOtherPaths, ...uploadedOtherPaths].slice(0, MAX_OTHER_IMAGES);
        }

        if (Object.keys(updates).length > 0) {
          await dataService.updateTransaction(transactionIdToSave, updates);
          if (onTransactionsUpdate) {
            onTransactionsUpdate(
              transactions.map((transaction) =>
                transaction.id === transactionIdToSave
                  ? { ...transaction, ...updates, updated_at: new Date().toISOString() }
                  : transaction
              )
            );
          }
        }
      }

      navigate("/");
    } catch (error) {
      console.error("Error saving card:", error);
      setErrorMessage(isCreateMode ? "Failed to add card." : "Failed to update card.");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isCreateMode && !editingTransaction) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate("/")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to inventory
        </Button>
        <div className="rounded-lg border border-border bg-muted/20 p-6">
          <p className="text-sm text-muted-foreground">Card not found.</p>
        </div>
      </div>
    );
  }

  const existingImageCards = [
    editData.frontImagePath ? { key: editData.frontImagePath, label: "Front" } : null,
    editData.backImagePath ? { key: editData.backImagePath, label: "Back" } : null,
    ...editData.otherImagePaths.map((path, index) => ({ key: path, label: `Other ${index + 1}` })),
  ].filter(Boolean) as Array<{ key: string; label: string }>;

  const imageCards = existingImageCards
    .map((image) => ({ label: image.label, src: existingImageUrls[image.key] || "" }))
    .filter((image) => Boolean(image.src));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold">{isCreateMode ? "Add New Card" : "Edit Card"}</h1>
          <p className="text-sm text-muted-foreground">
            {isCreateMode ? "Create a new inventory card." : "Update card details and photos."}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate("/")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to inventory
        </Button>
      </div>

      <div className="rounded-xl border border-border bg-card p-4 sm:p-6">
        <div className="grid gap-6 lg:grid-cols-[minmax(280px,360px)_1fr]">
          <div className="space-y-4">
            <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
              <p className="text-sm font-medium text-foreground">Photos</p>
              {imageCards.length > 0 && (
                <div className="grid gap-3">
                  {imageCards.map((image) => (
                    <button
                      key={image.label}
                      type="button"
                      onClick={() => setSelectedImage(image)}
                      className="space-y-2 text-left"
                    >
                      <div className="text-xs font-medium text-muted-foreground">{image.label}</div>
                      <img
                        src={image.src}
                        alt={image.label}
                        className="h-48 w-full rounded-md border border-border object-contain bg-background/80"
                      />
                    </button>
                  ))}
                </div>
              )}
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-front-image" className="text-sm font-normal">Front</Label>
                  {editData.frontImagePath && (
                    <div className="flex items-center justify-between rounded-md border border-border px-2 py-1 text-xs text-muted-foreground">
                      <span>Front image on file</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditData((prev) => ({ ...prev, frontImagePath: null, frontImageFile: null }))}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                  <Input
                    id="edit-front-image"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setEditData((prev) => ({ ...prev, frontImageFile: e.target.files?.[0] || null }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-back-image" className="text-sm font-normal">Back</Label>
                  {editData.backImagePath && (
                    <div className="flex items-center justify-between rounded-md border border-border px-2 py-1 text-xs text-muted-foreground">
                      <span>Back image on file</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditData((prev) => ({ ...prev, backImagePath: null, backImageFile: null }))}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                  <Input
                    id="edit-back-image"
                    type="file"
                    accept="image/*"
                    onChange={(e) => setEditData((prev) => ({ ...prev, backImageFile: e.target.files?.[0] || null }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-other-images" className="text-sm font-normal">Other (up to 3)</Label>
                {editData.otherImagePaths.length > 0 && (
                  <div className="space-y-2">
                    {editData.otherImagePaths.map((path) => (
                      <div key={path} className="flex items-center justify-between rounded-md border border-border px-2 py-1 text-xs text-muted-foreground">
                        <span>Other image on file</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setEditData((prev) => ({
                              ...prev,
                              otherImagePaths: prev.otherImagePaths.filter((item) => item !== path),
                            }))
                          }
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <Input
                  id="edit-other-images"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const availableSlots = Math.max(0, MAX_OTHER_IMAGES - editData.otherImagePaths.length);
                    const files = Array.from(e.target.files || []).slice(0, availableSlots);
                    setEditData((prev) => ({ ...prev, otherImageFiles: files }));
                  }}
                />
                {editData.otherImageFiles.length > 0 && (
                  <p className="text-xs text-muted-foreground">{editData.otherImageFiles.length} selected</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-card">Card name *</Label>
              <Input
                id="edit-card"
                value={editData.card}
                onChange={(e) => setEditData((prev) => ({ ...prev, card: e.target.value }))}
                placeholder="Listed name"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-purchaseDate">Purchase date *</Label>
                <Input
                  id="edit-purchaseDate"
                  type="date"
                  value={editData.purchaseDate}
                  onChange={(e) => setEditData((prev) => ({ ...prev, purchaseDate: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-purchasePrice">Purchase price *</Label>
                <Input
                  id="edit-purchasePrice"
                  type="number"
                  step="0.01"
                  value={editData.purchasePrice}
                  onChange={(e) => setEditData((prev) => ({ ...prev, purchasePrice: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              {isCreateMode && (
                <div className="space-y-2">
                  <Label htmlFor="edit-numberOfCards">Copies</Label>
                  <Input
                    id="edit-numberOfCards"
                    type="number"
                    min="1"
                    max={MAX_TOTAL_CARDS}
                    value={editData.numberOfCards}
                    onChange={(e) =>
                      setEditData((prev) => ({
                        ...prev,
                        numberOfCards: Math.min(
                          MAX_TOTAL_CARDS,
                          Math.max(1, parseInt(e.target.value, 10) || 1)
                        ),
                      }))
                    }
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="edit-purchasePlatform">Purchase platform</Label>
                <Input
                  id="edit-purchasePlatform"
                  value={editData.purchasePlatform}
                  onChange={(e) => setEditData((prev) => ({ ...prev, purchasePlatform: e.target.value }))}
                  placeholder="e.g., eBay"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-boughtFrom">Bought from</Label>
                <Input
                  id="edit-boughtFrom"
                  value={editData.boughtFrom}
                  onChange={(e) => setEditData((prev) => ({ ...prev, boughtFrom: e.target.value }))}
                  placeholder="Seller or shop"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="edit-sport">Sport / TCG</Label>
                <Input
                  id="edit-sport"
                  value={editData.sport}
                  onChange={(e) => setEditData((prev) => ({ ...prev, sport: e.target.value }))}
                  placeholder="e.g., Basketball"
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-year">Year</Label>
                <Input
                  id="edit-year"
                  type="number"
                  value={editData.year}
                  onChange={(e) => setEditData((prev) => ({ ...prev, year: e.target.value }))}
                  placeholder="e.g., 2023"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-manufacturer">Manufacturer</Label>
                <Input
                  id="edit-manufacturer"
                  value={editData.manufacturer}
                  onChange={(e) => setEditData((prev) => ({ ...prev, manufacturer: e.target.value }))}
                  placeholder="e.g., Topps"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="edit-set">Product / main set</Label>
                <Input
                  id="edit-set"
                  value={editData.set}
                  onChange={(e) => setEditData((prev) => ({ ...prev, set: e.target.value }))}
                  placeholder="e.g., Topps Chrome"
                />
              </div>
            </div>

            <CardCatalogFields
              idPrefix="edit"
              values={{
                card_number: editData.card_number,
                subject: editData.subject,
                league: editData.league,
                insert_name: editData.insert_name,
                parallel: editData.parallel,
                numbering: editData.numbering,
              }}
              onFieldChange={(key, value) => setEditData((prev) => ({ ...prev, [key]: value }))}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-grade">Grade</Label>
                <Input
                  id="edit-grade"
                  value={editData.grade}
                  onChange={(e) => setEditData((prev) => ({ ...prev, grade: e.target.value }))}
                  placeholder="e.g., PSA 10"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-gradingOrderId">Grading order ID</Label>
                <Input
                  id="edit-gradingOrderId"
                  value={editData.gradingOrderId}
                  onChange={(e) => setEditData((prev) => ({ ...prev, gradingOrderId: e.target.value }))}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-other">Other info</Label>
              <Input
                id="edit-other"
                value={editData.other}
                onChange={(e) => setEditData((prev) => ({ ...prev, other: e.target.value }))}
                placeholder="Catch-all"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={editData.notes}
                onChange={(e) => setEditData((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Private notes"
                rows={3}
              />
            </div>
          </div>
        </div>

        {errorMessage && (
          <p className="mt-4 text-sm text-destructive">{errorMessage}</p>
        )}

        <div className="mt-6 flex justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => navigate("/")}>
            Cancel
          </Button>
          <Button variant="gradient" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : isCreateMode ? "Add Card" : "Save Changes"}
          </Button>
        </div>
      </div>

      {selectedImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background/95 p-6 backdrop-blur">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="absolute right-6 top-6"
            onClick={() => setSelectedImage(null)}
          >
            <X className="mr-1 h-4 w-4" />
            Close
          </Button>
          <div className="max-w-6xl w-full">
            <p className="mb-3 text-sm text-muted-foreground">{selectedImage.label}</p>
            <img
              src={selectedImage.src}
              alt={selectedImage.label}
              className="max-h-[82vh] w-full rounded-lg object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EditInventoryCard;
